<script setup lang="ts">
import { useGetMe } from "@/auth/useMe";
import { useGetCanvassHouse } from "@/canvasses/canvassHousesCrud";
import type { Canvass } from "@/canvasses/types";
import HandleQueryStatus from "@/components/HandleQueryStatus.vue";
import ConversationContentView from "@/conversations/ConversationContentView.vue";
import ConversationDetails from "@/conversations/ConversationDetails.vue";
import ConversationEditForm from "@/conversations/ConversationEditForm.vue";
import {
  useDeleteConversation,
  useGetConversation,
  useUpdateConversation,
} from "@/conversations/conversationsCrud";
import { useConversationPermissions } from "@/conversations/useConversationPermissions";
import { computed, ref, type Ref } from "vue";
import { useRouter } from "vue-router";
import CanvassSatisfiedTargetsForm from "./CanvassSatisfiedTargetsForm.vue";
import ViewHeader from "./ViewHeader.vue";

const { houseId, conversationId, canvass } = defineProps<{
  canvass: Canvass;
  houseId: number;
  conversationId: string;
}>();

const router = useRouter();

const { canvassHouse } = useGetCanvassHouse(canvass.id, houseId);
const { conversation, getConversationStatus } =
  useGetConversation(conversationId);
const { me } = useGetMe();
const {
  updateConversation,
  status: updateStatus,
  error: updateError,
} = useUpdateConversation(houseId);
const { deleteConversation, status: deleteStatus } =
  useDeleteConversation(houseId);

const { canEdit, canDelete, canSetContent } = useConversationPermissions(
  me,
  conversation,
);

const isEditing = ref(false);
const editText = ref("");
const editSatisfiedCanvassTargetIds: Ref<Array<string>> = ref([]);
const satisfiedCanvassTargets = computed(() =>
  canvass.canvassTargetsWithProgress.filter(({ id }) =>
    conversation.value?.satisfiedCanvassTargetIds.includes(id),
  ),
);

// TODO clean this up - why do we need to toggle an editing state after navigating to the editing page?
const startEditing = () => {
  editText.value = conversation.value?.content ?? "";
  editSatisfiedCanvassTargetIds.value = [
    ...(conversation.value?.satisfiedCanvassTargetIds ?? []),
  ];
  isEditing.value = true;
};

const cancelEditing = () => {
  isEditing.value = false;
  editText.value = "";
  editSatisfiedCanvassTargetIds.value = [];
};

const saveEdit = () => {
  updateConversation(
    {
      id: conversationId,
      ...(canSetContent.value ? { content: editText.value } : {}),
      satisfiedCanvassTargetIds: editSatisfiedCanvassTargetIds.value,
    },
    {
      onSuccess: () => {
        isEditing.value = false;
        editText.value = "";
      },
    },
  );
};

const handleDelete = () => {
  deleteConversation(conversationId, {
    onSuccess: () => router.push({ name: "canvassFlowHouseConversations" }),
  });
};
</script>

<template>
  <ViewHeader
    :redirect-on-close-target="{
      name: 'canvassFlowHouseConversations',
    }"
  >
    Gespräch bearbeiten
    <template v-if="canvassHouse" #subheading>
      Adresse: {{ canvassHouse.street }} {{ canvassHouse.houseNumber }}
    </template>
  </ViewHeader>
  <div class="column container container-md">
    <HandleQueryStatus :query-status="getConversationStatus">
      <div v-if="conversation" class="box column">
        <ConversationDetails :conversation="conversation" />

        <template v-if="isEditing">
          <CanvassSatisfiedTargetsForm
            v-if="canvass.canvassTargetsWithProgress.length > 0"
            v-model:satisfied-canvass-target-ids="editSatisfiedCanvassTargetIds"
            :canvass-targets="canvass.canvassTargetsWithProgress"
          />
          <ConversationEditForm
            v-model="editText"
            :saving="updateStatus === 'pending'"
            :hide-content="!canSetContent"
            @submit="saveEdit"
            @cancel="cancelEditing"
          />
          <small v-if="updateError" class="error">
            {{ updateError.message }}
          </small>
        </template>
        <template v-else>
          <section
            v-if="canvass.canvassTargetsWithProgress.length > 0"
            class="column"
          >
            <h3>Gesprächsziele</h3>
            <p class="muted">
              {{ satisfiedCanvassTargets.length }} von
              {{ canvass.canvassTargetsWithProgress.length }} Zielen erreicht!
            </p>
            <div class="row">
              <span
                v-for="satisfiedCanvassTarget in satisfiedCanvassTargets"
                :key="satisfiedCanvassTarget.id"
                class="badge"
                >{{ satisfiedCanvassTarget.description }}</span
              >
            </div>
          </section>
          <ConversationContentView
            :conversation="conversation"
            :can-edit="canEdit"
            :can-delete="canDelete"
            :delete-status="deleteStatus"
            :hide-content="!canSetContent"
            @edit="startEditing"
            @delete="handleDelete"
          />
        </template>
      </div>
    </HandleQueryStatus>
  </div>
</template>
